// Kitchen API Service — Web version
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : 'https://biryani.com/api';

// The host-only part (no /api suffix), used for serving uploaded files
export const API_HOST = process.env.REACT_APP_API_URL || 'https://biryani.com';

/**
 * Resolve an image URL returned from the backend.
 * - Relative paths like "/api/menu/images/..." or "/uploads/menu/..." → prefix API_HOST
 * - Old absolute URLs with internal IPs → strip host and re-prefix with API_HOST
 */
export function resolveImageUrl(url) {
  if (!url) return null;
  // Normalise legacy /uploads/ paths → /api/uploads/ so they go through the API proxy
  const normalised = url.replace(/^(https?:\/\/[^/]+)?\/uploads\//, (_, host) =>
    host ? `${host}/api/uploads/` : '/api/uploads/'
  );
  if (normalised.startsWith('http://') || normalised.startsWith('https://')) {
    // Could be a stale URL with internal Docker IP — always strip and re-prefix
    try {
      const parsed = new URL(normalised);
      return `${API_HOST}${parsed.pathname}`;
    } catch {
      return normalised;
    }
  }
  // Relative path — prepend the API host
  return `${API_HOST}${normalised.startsWith('/') ? '' : '/'}${normalised}`;
}

const STORAGE_KEY = 'kitchen_auth_token';
const REFRESH_KEY = 'kitchen_refresh_token';
const ROLE_KEY    = 'kitchen_user_role';
const PERMS_KEY   = 'kitchen_user_permissions';
const KID_KEY     = 'kitchen_id';

class KitchenApiService {
  constructor() {
    this.token = null;
    this._unauthorizedCallback = null;

    this.api = axios.create({
      baseURL: API_BASE,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });

    // ── Intercept requests: when the body is FormData, delete the default
    //    Content-Type so the browser can set it with the correct multipart boundary.
    this.api.interceptors.request.use((config) => {
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
      return config;
    });

    this.api.interceptors.request.use(async (config) => {
      if (!this.token) this.token = this.getToken();
      if (this.token) config.headers.Authorization = `Bearer ${this.token}`;
      return config;
    });

    this.api.interceptors.response.use(
      (res) => res,
      async (error) => {
        if (error.response?.status === 401 && error.config?.url !== '/auth/refresh') {
          const refreshToken = this.getRefreshToken();
          if (refreshToken) {
            try {
              const r = await this.api.post('/auth/refresh', {}, {
                headers: { Authorization: `Bearer ${refreshToken}` },
              });
              const newToken = r.data?.data?.access_token;
              if (newToken) {
                this.setToken(newToken);
                error.config.headers.Authorization = `Bearer ${newToken}`;
                return this.api.request(error.config);
              }
            } catch (_) {}
          }
          this.clearToken();
          this._unauthorizedCallback?.();
        }
        return Promise.reject(error);
      }
    );
  }

  onUnauthorized(cb) { this._unauthorizedCallback = cb; }

  // ── Token helpers ────────────────────────────────────────────────────────────
  setToken(token) {
    this.token = token;
    localStorage.setItem(STORAGE_KEY, token);
  }
  setRefreshToken(token) { localStorage.setItem(REFRESH_KEY, token); }
  getToken()             { return localStorage.getItem(STORAGE_KEY); }
  getRefreshToken()      { return localStorage.getItem(REFRESH_KEY); }
  clearToken() {
    this.token = null;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PERMS_KEY);
    localStorage.removeItem(KID_KEY);
  }
  checkAuthStatus()      { return !!this.getToken(); }

  saveUserMeta(user) {
    if (user?.role)        localStorage.setItem(ROLE_KEY, user.role);
    if (user?.kitchen_id)  localStorage.setItem(KID_KEY, String(user.kitchen_id));
    if (user?.permissions) localStorage.setItem(PERMS_KEY, JSON.stringify(user.permissions));
  }
  getUserRole()        { return localStorage.getItem(ROLE_KEY); }
  getUserPermissions() {
    try { return JSON.parse(localStorage.getItem(PERMS_KEY) || '[]'); } catch { return []; }
  }
  getKitchenId()       { return localStorage.getItem(KID_KEY); }

  // ── HTTP helpers ─────────────────────────────────────────────────────────────
  async _get(url, config = {}) {
    try {
      const r = await this.api.get(url, config);
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }
  async _post(url, data = {}, config = {}) {
    try {
      const r = await this.api.post(url, data, config);
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }
  async _put(url, data = {}, config = {}) {
    try {
      const r = await this.api.put(url, data, config);
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }
  async _patch(url, data = {}, config = {}) {
    try {
      const r = await this.api.patch(url, data, config);
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }
  async _delete(url, config = {}) {
    try {
      const r = await this.api.delete(url, config);
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }

  // Multipart helpers — Content-Type is stripped by the request interceptor above
  async _postMultipart(url, formData) {
    try {
      const r = await this.api.post(url, formData, {
        headers: { 'Content-Type': undefined },
        transformRequest: [(data) => data], // Prevent axios from JSON-encoding FormData
      });
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }
  async _putMultipart(url, formData) {
    try {
      const r = await this.api.put(url, formData, {
        headers: { 'Content-Type': undefined },
        transformRequest: [(data) => data], // Prevent axios from JSON-encoding FormData
      });
      return { success: true, data: r.data?.data ?? r.data };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.response?.data?.message || e.message || 'Request failed' };
    }
  }

  // ── Auth ─────────────────────────────────────────────────────────────────────
  async sendKitchenOTP(phone, countryCode) {
    return this._post('/auth/kitchen/send-otp', { phone, country_code: countryCode });
  }

  async verifyKitchenOTP(phone, countryCode, otp) {
    return this._post('/auth/kitchen/verify-otp', { phone, country_code: countryCode, otp });
  }

  async kitchenStaffLogin(payload) {
    const res = await this._post('/auth/staff/login', payload);
    if (res.success) {
      const d = res.data?.data || res.data;
      const token = d?.access_token;
      if (token) {
        this.setToken(token);
        if (d?.refresh_token) this.setRefreshToken(d.refresh_token);
        this.saveUserMeta(d?.user);
      }
    }
    return res;
  }

  async completeKitchenRegistration(formData) {
    try {
      // Don't set Content-Type header - axios will set it automatically with boundary
      const r = await this.api.post('/auth/kitchen/register', formData, {
        timeout: 60000,
      });
      if (r.data.success) {
        const token = r.data.data?.access_token;
        if (token) {
          this.setToken(token);
          if (r.data.data?.refresh_token) this.setRefreshToken(r.data.data.refresh_token);
          this.saveUserMeta(r.data.data?.user);
        }
        return { success: true, data: r.data.data };
      }
      return { success: false, error: r.data.error || 'Registration failed.' };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || 'Registration failed.' };
    }
  }

  async getCurrentUser() {
    return this._get('/auth/staff/me');
  }

  async logout() {
    try { await this.api.post('/auth/logout'); } catch (_) {}
    this.clearToken();
  }

  // ── Profile ──────────────────────────────────────────────────────────────────
  async updateKitchenProfile(data) {
    return this._put('/kitchen/info', data);
  }
  async uploadKitchenImage(type, file) {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('type', type);
    // Don't set Content-Type header - axios will set it automatically with boundary
    return this._post('/kitchen/info/image', fd);
  }
  async getKitchenVerification() {
    return this._get('/kitchen/info');
  }

  // ── Dashboard ────────────────────────────────────────────────────────────────
  async getOrdersDashboard() {
    return this._get('/orders/dashboard');
  }
  async getPerformanceDashboard(period = 'today') {
    return this._get(`/analytics/performance?period=${period}`);
  }

  // ── Orders ───────────────────────────────────────────────────────────────────
  async getOrdersByStatus(status) {
    return this._get(`/orders/status/${status}`);
  }
  async getOrderDetails(orderId) {
    return this._get(`/orders/${orderId}/details`);
  }
  async acceptOrder(orderId, estimatedTime) {
    return this._put(`/orders/${orderId}/accept`, { estimated_time: estimatedTime });
  }
  async rejectOrder(orderId, reason) {
    return this._put(`/orders/${orderId}/reject`, { reason });
  }
  async updateOrderStatus(orderId, status, notes = null) {
    return this._put(`/orders/${orderId}/start-preparation`, { status, notes });
  }
  async markOrderReady(orderId) {
    return this._put(`/orders/${orderId}/mark-ready`);
  }
  async getOrderHistory(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this._get(`/orders/status/completed${params ? '?' + params : ''}`);
  }
  async completeOrderWithProof(orderId, proofData) {
    const fd = new FormData();
    if (proofData.image) fd.append('proof_image', proofData.image);
    if (proofData.notes) fd.append('notes', proofData.notes);
    // Don't set Content-Type header - axios will set it automatically with boundary
    return this._post(`/orders/${orderId}/complete-with-proof`, fd);
  }

  // ── Menu ─────────────────────────────────────────────────────────────────────
  async getMenuOverview() {
    return this._get('/menu/items');
  }
  async getMenuCategories() {
    return this._get('/menu/categories');
  }
  async createMenuCategory(data) {
    return this._post('/menu/categories', data);
  }
  async updateMenuCategory(categoryId, data) {
    return this._put(`/menu/categories/${categoryId}`, data);
  }
  async deleteMenuCategory(categoryId) {
    return this._delete(`/menu/categories/${categoryId}`);
  }
  async getMenuItemDetails(itemId) {
    return this._get(`/menu/items/${itemId}`);
  }
  async addMenuItem(data) {
    if (data instanceof FormData) {
      console.log('[MENU IMAGE] addMenuItem — sending as multipart/form-data');
      if (data.get('image')) {
        const f = data.get('image');
        console.log(`[MENU IMAGE] File attached: name=${f.name}, type=${f.type}, size=${f.size}`);
      }
      // Explicitly clear Content-Type so the browser sets the multipart boundary
      return this._postMultipart('/menu/items', data);
    }
    return this._post('/menu/items', data);
  }
  async updateMenuItem(itemId, data) {
    if (data instanceof FormData) {
      console.log('[MENU IMAGE] updateMenuItem — sending as multipart/form-data');
      if (data.get('image')) {
        const f = data.get('image');
        console.log(`[MENU IMAGE] File attached: name=${f.name}, type=${f.type}, size=${f.size}`);
      } else {
        console.log('[MENU IMAGE] updateMenuItem — no new image, keeping existing');
      }
      return this._putMultipart(`/menu/items/${itemId}`, data);
    }
    return this._put(`/menu/items/${itemId}`, data);
  }
  async deleteMenuItem(itemId) {
    return this._delete(`/menu/items/${itemId}`);
  }
  async toggleMenuItemAvailability(itemId, isAvailable) {
    return this._patch(`/menu/items/${itemId}/availability`, { is_available: isAvailable });
  }

  /**
   * Bulk-upload menu items from a CSV/XLS/XLSX file.
   * @param {File} file            – the raw File object from an <input type="file">
   * @param {object} opts
   * @param {boolean} opts.dryRun  – true → preview only, no DB writes
   * @param {string}  opts.duplicateHandling – 'skip' | 'update' | 'duplicate'
   * @param {function} opts.onProgress – optional (progressEvent) => void
   */
  async bulkUploadMenu(file, { dryRun = false, duplicateHandling = 'skip', onProgress } = {}) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      fd.append('duplicate_handling', duplicateHandling);

      const r = await this.api.post('/menu/bulk-upload', fd, {
        headers: { 'Content-Type': undefined },
        transformRequest: [(data) => data],
        timeout: 120000,
        onUploadProgress: onProgress,
      });
      return { success: true, data: r.data };
    } catch (e) {
      return {
        success: false,
        error: e.response?.data?.error || e.response?.data?.message || e.message || 'Bulk upload failed',
        data: e.response?.data,
      };
    }
  }
  async getModifierGroups() {
    return this._get('/menu/modifier-groups');
  }
  async createModifierGroup(data) {
    return this._post('/menu/modifier-groups', data);
  }
  async updateModifierGroup(groupId, data) {
    return this._put(`/menu/modifier-groups/${groupId}`, data);
  }
  async deleteModifierGroup(groupId) {
    return this._delete(`/menu/modifier-groups/${groupId}`);
  }
  async getMenuAnalytics() {
    return this._get('/analytics/orders');
  }
  async getTaxConfig() {
    return this._get('/menu/tax-config');
  }
  async updateTaxConfig(data) {
    return this._put('/menu/tax-config', data);
  }

  // ── Schedule ─────────────────────────────────────────────────────────────────
  async getKitchenConfig() {
    return this._get('/kitchen/config');
  }
  async updateKitchenConfig(data) {
    return this._put('/kitchen/config', data);
  }
  async toggleKitchenOpen(isOpen) {
    return this._post('/kitchen/toggle-open', { is_open: isOpen });
  }
  async getKitchenTimeSlots() {
    return this._get('/kitchen/time-slots');
  }
  async updateKitchenTimeSlots(slots) {
    return this._put('/kitchen/time-slots', { slots });
  }
  async getBlockedPeriods(options = {}) {
    const p = new URLSearchParams(options).toString();
    return this._get(`/blocked-periods/${p ? '?' + p : ''}`);
  }
  async createBlockedPeriod(data) {
    return this._post('/blocked-periods/', data);
  }
  async updateBlockedPeriod(id, data) {
    return this._put(`/blocked-periods/${id}`, data);
  }
  async deleteBlockedPeriod(id) {
    return this._delete(`/blocked-periods/${id}`);
  }
  async getOrderSlotCapacity(date, view = 'day') {
    return this._get(`/kitchen/pickup-slots/preview?date=${date}&view=${view}`);
  }
  async generateScheduleSlots(date, startTime = '10:00', endTime = '22:00', slotDuration = 30, capacity = 8) {
    return this._put('/kitchen/pickup-slots/config', { date, start_time: startTime, end_time: endTime, slot_duration: slotDuration, capacity });
  }
  async blockTimeSlot(slotId, reason = '') {
    return this._put('/kitchen/pickup-slots/blocked', { slot_id: slotId, reason });
  }
  async unblockTimeSlot(slotId) {
    return this._put('/kitchen/pickup-slots/blocked', { slot_id: slotId, unblock: true });
  }

  // ── Analytics ────────────────────────────────────────────────────────────────
  async getFinancialReports(period = 'month', reportType = 'revenue') {
    return this._get(`/analytics/financial?period=${period}&report_type=${reportType}`);
  }
  async exportFinancialReport(period, reportType, format = 'csv') {
    return this._get(`/analytics/reports?period=${period}&report_type=${reportType}&format=${format}`);
  }
  async getCustomerAnalytics(period = 'month', segment = 'all') {
    return this._get(`/analytics/customers?period=${period}&segment=${segment}`);
  }

  // ── Team Management ──────────────────────────────────────────────────────────
  async getKitchenUsers() {
    return this._get('/kitchen-users');
  }
  async createKitchenUser(data) {
    return this._post('/kitchen-users', data);
  }
  async updateKitchenUser(userId, data) {
    return this._put(`/kitchen-users/${userId}`, data);
  }
  async deleteKitchenUser(userId) {
    return this._delete(`/kitchen-users/${userId}`);
  }
  async toggleKitchenUserStatus(userId, isActive) {
    return this._patch(`/kitchen-users/${userId}`, { is_active: isActive });
  }
  async resetKitchenUserPassword(userId, newPassword) {
    return this._post(`/kitchen-users/${userId}/reset-password`, { new_password: newPassword });
  }

  // ── Notifications ────────────────────────────────────────────────────────────
  async getNotifications(filter = 'all') {
    return this._get(`/notifications/?filter=${filter}`);
  }
  async markNotificationRead(id) {
    return this._put(`/notifications/${id}/read`);
  }
  async markAllNotificationsRead() {
    return this._put('/notifications/mark-all-read');
  }
  async deleteNotification(id) {
    return this._delete(`/notifications/${id}`);
  }

  // ── Ratings ──────────────────────────────────────────────────────────────────
  async getRatings(params = {}) {
    const p = new URLSearchParams(params).toString();
    return this._get(`/kitchen/ratings${p ? '?' + p : ''}`);
  }

  // ── Payout / Finance ─────────────────────────────────────────────────────────
  async getPayoutBalance() {
    const kid = this.getKitchenId();
    return this._get(`/kitchen/summary${kid ? '?kitchen_id=' + kid : ''}`);
  }
  async getPayoutTransactions(timeframe = 'week') {
    return this._get(`/analytics/financial?period=${timeframe}`);
  }
  async getPayoutAnalytics(timeframe = 'week') {
    return this._get(`/analytics/financial?period=${timeframe}`);
  }
  async initiateTransfer(data) {
    return this._post('/kitchen/info', data);
  }
  async getKitchenStatus() {
    return this._get('/kitchen/status');
  }
  async updateKitchenStatus(status) {
    return this._put('/kitchen/status', status);
  }
}

const kitchenApi = new KitchenApiService();
export default kitchenApi;
